import React from "react";
import { type RealtimeFeedItem } from "../../../../stores/RealtimeStore";
import styles from "./LastOperations.module.css";

export type LastOperationsStatusTone =
  | "success"
  | "warning"
  | "danger"
  | "muted";

interface LastOperationsOptions {
  events: RealtimeFeedItem[];
  status: string;
  statusTone: LastOperationsStatusTone;
  error: string | null;
  isLoading: boolean;
  showReconnectAction: boolean;
  canReconnect: boolean;
  reconnectCooldownSeconds: number;
  onReconnect: () => void;
  onOpenEvent: (event: RealtimeFeedItem) => void;
}

export const LastOperations: React.FC<LastOperationsOptions> = ({
  events,
  status,
  statusTone,
  error,
  isLoading,
  showReconnectAction,
  canReconnect,
  reconnectCooldownSeconds,
  onReconnect,
  onOpenEvent,
}) => {
  const reconnectButtonLabel = canReconnect
    ? "переподключить"
    : `повторить через ${Math.max(1, reconnectCooldownSeconds)}с`;

  const formatTime = (isoTimestamp: string): string => {
    const date = new Date(isoTimestamp);
    if (Number.isNaN(date.getTime())) {
      return isoTimestamp;
    }

    const now = new Date();
    const isSameDay =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();

    if (isSameDay) {
      return date.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    return date.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const statusClassName =
    statusTone === "success"
      ? styles.statusSuccess
      : statusTone === "warning"
        ? styles.statusWarning
        : statusTone === "danger"
          ? styles.statusDanger
          : styles.statusMuted;

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <strong className={styles.subtitle}>Последние события:</strong>
        <span className={`${styles.status} ${statusClassName}`}>{status}</span>
      </div>

      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          {showReconnectAction && (
            <button
              className={styles.retryButton}
              onClick={onReconnect}
              disabled={!canReconnect}
            >
              {reconnectButtonLabel}
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <p className={styles.empty}>Загружаем последние операции...</p>
      ) : events.length === 0 ? (
        <p className={styles.empty}>Событий пока нет</p>
      ) : (
        <ul className={styles.list}>
          {events.map((event) => {
            const isDeletedEntityEvent =
              event.type === "journal.destroy" ||
              (event.operation === "deleted" &&
                (event.entity === "products" || event.entity === "boxes"));
            const isNavigable =
              Boolean(event.productId || event.boxId) && !isDeletedEntityEvent;
            return (
              <li
                key={event.id}
                className={`${styles.item} ${
                  isNavigable ? styles.navigable : ""
                }`}
                role={isNavigable ? "button" : undefined}
                tabIndex={isNavigable ? 0 : undefined}
                onClick={() => {
                  if (isNavigable) {
                    onOpenEvent(event);
                  }
                }}
                onKeyDown={(keyEvent) => {
                  if (
                    isNavigable &&
                    (keyEvent.key === "Enter" || keyEvent.key === " ")
                  ) {
                    keyEvent.preventDefault();
                    onOpenEvent(event);
                  }
                }}
              >
                <div className={styles.itemHeader}>
                  <span
                    className={
                      event.tone === "warning" ? styles.warning : styles.info
                    }
                  >
                    {event.title}
                  </span>
                  <div className={styles.itemMeta}>
                    <span
                      className={
                        event.sourceType === "system"
                          ? `${styles.sourceBadge} ${styles.sourceSystem}`
                          : `${styles.sourceBadge} ${styles.sourceUser}`
                      }
                    >
                      {event.sourceType === "system"
                        ? "системное"
                        : "пользователь"}
                    </span>
                    <time>{formatTime(event.timestamp)}</time>
                  </div>
                </div>
                <div className={styles.description}>{event.description}</div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
